import { useEffect, useState } from 'react'
import {
  trackCollapsedPaintedDoorViewed,
  trackCollapsedPaintedDoorV2Viewed,
  trackI18nPricingT3Viewed,
} from '@mc/checkout'
import { Conditional, ShowIf } from '@mc/conditional-rendering'
import { useFreeTrial } from '@mc/free-trial'
import { Trans } from '@mc/i18n'
import { usePromotion } from '@mc/promo-utils'
import { useStaticData } from '@mc/static-data-react'
import { useIsVisible } from '@mc/visibility'
import { Pen5443StickyFooterMoneyBackWrapper } from 'experiments/pen-5443_v1_sticky_footer_money_back'
import { StaticData } from 'types/libs/staticData'
import { useMoneyBackPricingMicrocopy } from './useMoneyBackPricingMicrocopy'

export type PricingTextProps = {
  translationKey?:
    | 'long'
    | 'short'
    | 'shortPriceBold'
    | 'shortPriceStyled'
    | 'longWithSeries'
    | 'pen-5333_v1-qr-value-props-copy' // pen-5333_v1_qr_value_props_copy experiment
    | 'pen-5613_v1-cm-timeline-view-sticky-footer' // pen-5613_v1 cm_timeline_view experiment
    | 'pen-5613_v1-cm-innovative-swing' // pen-5613_v1 cm_innovative_swing experiment
    | 'pen-5646_v1-sticky-footer'
  children?: React.ReactNode
  className?: string
  isStickyFooter?: boolean
  // pen-5443_v1_sticky_footer_money_back experiment
  isStickyFooterMoneyBackExperiment?: boolean
}

const config = {
  threshold: 0.7,
  duration: { visible: 500 },
}

export const PricingText = ({
  translationKey = 'long',
  children,
  className,
  isStickyFooter = false,
  // pen-5443_v1_sticky_footer_money_back
  isStickyFooterMoneyBackExperiment = false,
}: PricingTextProps) => {
  const { annualPass } = useStaticData<StaticData>()
  const { trial } = useFreeTrial()
  const { isPromotionOn } = usePromotion()
  const isMoneyBackPricingPage = useMoneyBackPricingMicrocopy()

  const { ref } = useTrackingEntryPaintedDoorViewed()

  if (!annualPass) {
    return null
  }

  const price = annualPass.monetizedYearPricePerMonth
  const discountedPrice = annualPass.monetizedYearPricePerMonthDiscounted
  const hasDiscountedPrice = isPromotionOn && !!discountedPrice
  const showMoneyBackPricing = isMoneyBackPricingPage && !isStickyFooter

  const promoKey =
    translationKey === 'shortPriceStyled'
      ? 'styled.pricingTextPromo'
      : 'pricingTextPromo'

  const defaultPricingText = () => {
    if (!showMoneyBackPricing) {
      return (
        <Trans
          i18nKey={`common.pricing.${translationKey}`}
          values={{ price }}
          ns='@mc/next'
        >
          {children}
        </Trans>
      )
    }

    if (translationKey === 'shortPriceStyled') {
      return (
        <Trans
          i18nKey='common.pricing.styled.pricingText'
          values={{ price }}
          ns='@mc/next'
        >
          {children}
        </Trans>
      )
    }

    return (
      <Trans
        ns='@mc/next'
        i18nKey='common.pricing.pricingText'
        values={{ price }}
      />
    )
  }

  return (
    <span className={className} ref={ref}>
      <Conditional>
        <ShowIf
          condition={translationKey === 'pen-5613_v1-cm-innovative-swing'}
        >
          <Trans
            ns='@mc/next'
            i18nKey={`common.pricing.${translationKey}.${hasDiscountedPrice ? 'discount' : 'regular'}`}
            values={{
              price,
              newPrice: discountedPrice,
            }}
          >
            <span className='mc-text--strike' />
            <span className='mc-text--bold' />
          </Trans>
        </ShowIf>

        <ShowIf condition={trial.eligible}>
          <Trans
            ns='@mc/next'
            i18nKey='trial.pricing'
            values={{ trialType: trial.strings.type }}
          />
        </ShowIf>

        <ShowIf condition={hasDiscountedPrice}>
          <Trans
            i18nKey={
              showMoneyBackPricing
                ? `common.pricing.${promoKey}`
                : `common.pricing.discount.${translationKey}`
            }
            values={{
              strikeThroughPrice: annualPass.monetizedYearPricePerMonth,
              price: discountedPrice,
            }}
            ns='@mc/next'
          >
            <span className='mc-opacity--hinted mc-text--bold' />
            <span className='mc-opacity--hinted mc-text--strike' />
            <span className='mc-text--bold' />
          </Trans>
        </ShowIf>

        <ShowIf condition={isPromotionOn && !discountedPrice}>
          {defaultPricingText()}
        </ShowIf>

        <ShowIf condition={isStickyFooterMoneyBackExperiment}>
          <Pen5443StickyFooterMoneyBackWrapper control={defaultPricingText()} />
        </ShowIf>

        <ShowIf isDefault>{defaultPricingText()}</ShowIf>
      </Conditional>
    </span>
  )
}

const useTrackingEntryPaintedDoorViewed = () => {
  // prcng-107_v1 experiment
  // using the TrackingContainer is not possible here since this component
  // is being used inside <p> tags what triggers a react-hydration-error.
  // Workaround: replicate TrackingContainer behavior
  const { isVisible, ref } = useIsVisible<HTMLSpanElement>(config)
  const [hasTrackedView, setHasTrackedView] = useState(false)
  useEffect(() => {
    if (isVisible && !hasTrackedView) {
      setHasTrackedView(true)
      trackCollapsedPaintedDoorViewed()
      trackCollapsedPaintedDoorV2Viewed()
      trackI18nPricingT3Viewed()
    }
  }, [isVisible, hasTrackedView])

  return { ref }
}
